/**
 * PaymentsHub — iter-237.6 (Feb 2026)
 *
 * Root of the "المدفوعات" section under ASB606 Finance. Now shows only TWO
 * top-level buttons per user request:
 *   1) الإدراج المالي للشركة   → /finance/asb606/payments/entry
 *   2) السجل والمتابعة المالية  → /finance/asb606/payments/records
 *
 * Each button opens a dedicated hub that separates data-entry from
 * data-viewing (see FinancialEntryHub + FinancialRecordsHub).
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FilePlus2, ClipboardList, ArrowLeft, ArrowRight, Info } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { usePerms } from '../../permissions/PermissionsContext';

export default function PaymentsHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const CARDS = [
    {
      id: 'entry',
      number: 1,
      icon: FilePlus2,
      color: '#3B82F6',
      route: '/finance/asb606/payments/entry',
      titleAr: 'الإدراج المالي للشركة',
      titleEn: 'Financial Entry',
      descAr: 'إدراج الرواتب، المدفوعات، أصول الشركة، والمستحقات (بدون سجلات)',
      descEn: 'Add salaries, company payments, assets, and liabilities (entry only)',
      testid: 'payments-hub-entry',
    },
    {
      id: 'records',
      number: 2,
      icon: ClipboardList,
      color: '#10B981',
      route: '/finance/asb606/payments/records',
      titleAr: 'السجل والمتابعة المالية للشركة',
      titleEn: 'Financial Records & Tracking',
      descAr: 'عرض ومتابعة الرواتب، المدفوعات، الأصول، والمستحقات المسجّلة',
      descEn: 'View & track saved salaries, payments, assets, and liabilities',
      testid: 'payments-hub-records',
    },
  ];

  return (
    <DashboardLayout
      title="المدفوعات المالية للشركة"
      titleEn="Company Financials"
      subtitle="اختر بين إدراج بيانات جديدة أو عرض السجلات والمتابعة"
      subtitleEn="Choose between adding new data or viewing records"
      screenCode="ASB-FIN-PAY"
      backPath="/finance/asb606"
      onBack={() => navigate('/finance/asb606')}
      breadcrumb={[
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'ASB606 (المالية)', labelEn: 'ASB606 (Finance)', path: '/finance/asb606' },
        { labelAr: 'المدفوعات المالية للشركة', labelEn: 'Company Financials' },
      ]}
    >
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
        data-testid="payments-hub"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {visiblePages(CARDS, 'route').map((card, i) => {
            const Icon = card.icon;
            const Arrow = isAr ? ArrowLeft : ArrowRight;
            return (
              <motion.button
                key={card.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                whileHover={{ y: -5, scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => navigate(card.route)}
                className="group relative bg-white/[0.02] backdrop-blur-xl border rounded-2xl p-6 text-start transition-all overflow-hidden"
                style={{ borderColor: `${card.color}30` }}
                data-testid={card.testid}
              >
                <div
                  className="absolute top-3 start-3 w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs border"
                  style={{ backgroundColor: `${card.color}25`, borderColor: `${card.color}60`, color: card.color }}
                >
                  {card.number}
                </div>

                <div className="flex items-start gap-4 mt-4">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center border transition-transform group-hover:scale-110 shrink-0"
                    style={{ backgroundColor: `${card.color}15`, borderColor: `${card.color}40` }}
                  >
                    <Icon className="h-7 w-7" style={{ color: card.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                      {isAr ? card.titleAr : card.titleEn}
                      <Arrow className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: card.color }} />
                    </h3>
                    <p className="text-sm text-[#A1A1AA] leading-relaxed">
                      {isAr ? card.descAr : card.descEn}
                    </p>
                  </div>
                </div>

                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{ background: `radial-gradient(circle at 50% 50%, ${card.color}10 0%, transparent 70%)` }}
                />
              </motion.button>
            );
          })}
        </div>

        <div className="mt-6 bg-white/[0.02] border border-white/10 rounded-2xl p-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center shrink-0">
            <Info className="h-4 w-4 text-blue-400" />
          </div>
          <p className="text-xs text-[#A1A1AA] leading-relaxed">
            {isAr
              ? 'الإدراج والسجلات فُصلا في مسارين منفصلين — تحطّ البيانات في "الإدراج"، وتراجع كل شيء في "السجل والمتابعة".'
              : 'Entry and records are now split into two independent flows — add data in "Entry" and review everything in "Records & Tracking".'}
          </p>
        </div>
      </section>
    </DashboardLayout>
  );
}
